import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from "rxjs";
import { IPagingData } from "../../oex-ui-kit/components/grid/grid";
import { IUploadedFileInfo } from '../../oex-ui-kit/components/upload-input/upload-input.component';
import {
  DeploymentMetric,
  DictionaryKey,
  IActivityItem,
  IAdminPermission,
  IAdminPermissionResponse,
  IAdminRolesResponse,
  IAdminUser,
  IAdminUsersResponse,
  IBillingHistoryItem,
  IDeploymentEvent,
  IDeploymentLog,
  IMetricsResponse,
  IDeployment,
  IDeploymentBackup,
  IDeploymentKey,
  IGithubInstallUrlResponse,
  IDeploymentVariable,
  IDeploymentVariableSaveRequest,
  IDeploymentVariableValueResponse,
  IDeploymentPasswordsResponse,
  IDeploymentsResponse,
  IDictionaryResponse,
  IIdResponse,
  IOAuthUlrRequestParams,
  IOAuthUrlResponse,
  IState,
  IUserProfile,
  IBalanceChangeResponse,
  IAdminMember,
  IAdminDashboard,
  type IAdminNewsletter
} from "./api.types";
import { FilterComponent, type IFilter } from "../../oex-ui-kit/components/filter/filter.component";

const API_URL = '/api/';

@Injectable({
  providedIn: 'root'
})
export class APIService {
  private http = inject(HttpClient);

  get url() {
    return API_URL;
  }

  get userAvaUploadUrl() {
    return this.url + 'upload/avatar';
  }

  getFile(url: string) {
    return firstValueFrom(
      this.http.get(url, { responseType: 'text' })
    );
  }

  async getOAuthUrl(params: IOAuthUlrRequestParams) {
    return firstValueFrom(
      this.http.post<IOAuthUrlResponse>(this.url + 'oauth/url', params)
    );
  }

  async getState() {
    return firstValueFrom(this.http.get<IState>(this.url + 'state', { withCredentials: true })
    );
  }

  doLogout() {
    return firstValueFrom(
      this.http.get(this.url + 'oauth/logout')
    )
  }

  getUserAvatarUrl(fileInfo?: IUploadedFileInfo) {
    if (!fileInfo?.id) {
      return '';
    }
    return this.url + `files/user/${fileInfo.id}`;
  }

  async getDictionary(key: DictionaryKey[] | string, term = '') {
    const keys = Array.isArray(key) ? key : [key];
    return firstValueFrom(
      this.http.get<IDictionaryResponse>(this.url + `dictionary/${keys.join(',')}?search=${term}`)
    )
  }

  async getAllDictionaries() {
    return firstValueFrom(
      this.http.get<IDictionaryResponse>(this.url + `dictionary`)
    )
  }

  async getAdminRoles() {
    return firstValueFrom(
      this.http.get<IAdminRolesResponse>(this.url + `admin/role`)
    )
  }

  async saveAdminPermissions(perms: IAdminPermission[]) {
    return firstValueFrom(
      this.http.post<void>(this.url + `admin/permission`, { items: perms })
    );
  }

  async deleteAdminPermission(idPerm: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `admin/permission/${idPerm}`)
    );
  }

  async getAdminPermissionsList(sorting: string = '') {
    if (!sorting) {
      sorting = 'n.asc';
    }
    return firstValueFrom(
      this.http.get<IAdminPermissionResponse>(this.url + `admin/permission?sorting=${sorting}`)
    )
  }

  async getAdminUsersList(sorting: string = '', roles?: string[]) {
    if (!sorting) {
      sorting = 'n.asc';
    }
    return firstValueFrom(
      this.http.get<IAdminUsersResponse>(this.url + `admin/user?sorting=${sorting}&roles=${(roles || []).join(',')}`)
    )
  }

  async deleteAdminUser(userId: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `admin/user/${userId}`)
    );
  }

  async saveAdminUsers(users: IAdminUser[]) {
    return firstValueFrom(
      this.http.post<IAdminUser[]>(this.url + `admin/user`, { items: users })
    );
  }

  /** Start impersonation of the given user (admins only). Session cookie is set by the response */
  async startImpersonation(userId: number) {
    return firstValueFrom(
      this.http.post<void>(this.url + `admin/impersonation`, { user_id: userId }, { withCredentials: true })
    );
  }

  /** Stop impersonation and return to the admin session */
  async stopImpersonation() {
    return firstValueFrom(
      this.http.delete<void>(this.url + `admin/impersonation`, { withCredentials: true })
    );
  }

  async getMemberProfile() {
    return firstValueFrom(
      this.http.get<IUserProfile>(this.url + 'member/profile')
    );
  }

  async saveMemberProfile(data: IUserProfile) {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + 'member/profile', data)
    );
  }

  async getDeployments() {
    return firstValueFrom(
      this.http.get<IDeploymentsResponse>(this.url + 'deployment')
    );
  }

  async getBillingHistory() {
    return firstValueFrom(
      this.http.get<IPagingData<IBillingHistoryItem>>(this.url + 'billing_history')
    );
  }

  async getDeployment(id: number) {
    return firstValueFrom(
      this.http.get<IDeployment>(this.url + `deployment/${id}`)
    );
  }

  async getDeploymentLogs(deploymentId: number, search = '', filters: IFilter, page = 1, pageSize = 30) {
    return firstValueFrom(
      this.http.get<IPagingData<IDeploymentLog>>(this.url + `deployment/${deploymentId}/log?search=${search}&currPage=${page}&pageSize=${pageSize}` + FilterComponent.getFilterParams(filters))
    );
  }

  getDeploymentLogsExportUrl(deploymentId: number, search = '', filters: IFilter) {
    return this.url + `deployment/${deploymentId}/log/export?search=${search}` + FilterComponent.getFilterParams(filters);
  }

  async getDeploymentEvents(deploymentId: number, filters: IFilter, page = 1, pageSize = 30) {
    return firstValueFrom(
      this.http.get<IPagingData<IDeploymentEvent>>(this.url + `deployment/${deploymentId}/event?currPage=${page}&pageSize=${pageSize}` + FilterComponent.getFilterParams(filters))
    );
  }

  async saveDeployment(deployment: IDeployment) {
    return firstValueFrom(
      this.http.post<IDeployment>(this.url + 'deployment', deployment)
    );
  }

  async deleteDeployment(id: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `deployment/${id}`)
    );
  }

  async startDeployment(id: number) {
    return firstValueFrom(
      this.http.post<IDeployment>(this.url + `deployment/${id}/start`, {})
    );
  }

  async stopDeployment(id: number) {
    return firstValueFrom(
      this.http.post<IDeployment>(this.url + `deployment/${id}/stop`, {})
    );
  }

  async restartDeployment(id: number) {
    return firstValueFrom(
      this.http.post<IDeployment>(this.url + `deployment/${id}/restart`, {})
    );
  }

  async getDeploymentBackups(id: number) {
    return firstValueFrom(
      this.http.get<IDeploymentBackup[]>(this.url + `deployment/${id}/backup`)
    );
  }

  async restoreDeploymentBackup(idDeployment: number, backupName: string) {
    return firstValueFrom(
      this.http.post<void>(this.url + `deployment/${idDeployment}/backup/${backupName}/restore`, {})
    );
  }

  async isDomainAvailable(domain: string, idDeployment?: number) {
    return firstValueFrom(
      this.http.get<{ is_available: boolean }>(this.url + `domain/${domain}/is_available?id=${idDeployment}`)
    );
  }

  async getDeploymentKeys(deploymentId: number) {
    return firstValueFrom(
      this.http.get<IDeploymentKey[]>(this.url + `deployment/${deploymentId}/key`)
    );
  }

  async getDeploymentVariables(deploymentId: number) {
    return firstValueFrom(
      this.http.get<IDeploymentVariable[]>(this.url + `deployment/${deploymentId}/vars`)
    );
  }

  async saveDeploymentVariables(deploymentId: number, variables: IDeploymentVariableSaveRequest[]) {
    return firstValueFrom(
      this.http.post<IDeploymentVariable[]>(this.url + `deployment/${deploymentId}/vars`, variables)
    );
  }

  async getDeploymentVariableValue(deploymentId: number, variableId: number) {
    return firstValueFrom(
      this.http.get<IDeploymentVariableValueResponse>(this.url + `deployment/${deploymentId}/vars/${variableId}/value`)
    );
  }

  async getDeploymentPasswords(deploymentId: number) {
    return firstValueFrom(
      this.http.get<IDeploymentPasswordsResponse>(this.url + `deployment/${deploymentId}/irispassword`)
    );
  }

  async createDeploymentKey(deploymentId: number) {
    return firstValueFrom(
      this.http.post(this.url + `deployment/${deploymentId}/key`, {}, {
        responseType: 'blob',
        observe: 'response'
      })
    );
  }

  async deleteDeploymentKey(deploymentId: number, keyId: string) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `deployment/${deploymentId}/key/${keyId}`)
    );
  }

  async getGithubInstallUrl(deploymentId: number) {
    return firstValueFrom(
      this.http.get<IGithubInstallUrlResponse>(this.url + `deployment/${deploymentId}/github/install_url`)
    );
  }

  async disconnectGithub(deploymentId: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `deployment/${deploymentId}/github`)
    );
  }

  async getLastActivity(page = 1, sorting = 'd.asc', pageSize = 40) {
    return firstValueFrom(
      this.http.get<IPagingData<IActivityItem>>(this.url + 'events' + `?pageSize=${pageSize}&currPage=${page}&sorting=${sorting}`)
    );
  }

  async getDeploymentMetrics(deploymentId: number, metrics: DeploymentMetric[], start: string, end: string, last = '', step = '1h') {
    return firstValueFrom(
      this.http.get<IMetricsResponse>(this.url + `deployment/${deploymentId}/monitoring/${metrics.join(',')}?start=${start}&end=${end}&last=${last}&step=${step}`)
    );
  }

  async adminAdjustPoints(idMember: number, amount: number ) {
    return firstValueFrom(
      this.http.post<IBalanceChangeResponse>(this.url + `admin/member/${idMember}/adjust_balance`, {amount})
    );
  }

  async getAdminMembers(sorting: string | 'n.asc' | 'n.desc' | 'e.asc' | 'e.desc' = '', page = 1, pageSize = 30) {
    if (!sorting) {
      sorting = 'n.asc';
    }
    return firstValueFrom(
      this.http.get<IPagingData<IAdminMember>>(this.url + `admin/member?sorting=${sorting}&currPage=${page}&pageSize=${pageSize}`)
    )
  }

  async deleteAdminMember(memberId: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `admin/member/${memberId}`)
    );
  }

  async blockAdminMember(idMember: number) {
    return firstValueFrom(
      this.http.post<void>(this.url + `admin/member/${idMember}/block`, {})
    );
  }

  async saveAdminMembers(members: IAdminMember[]) {
    return firstValueFrom(
      this.http.post<IAdminMember[]>(this.url + `admin/member`, { items: members })
    );
  }

  async createTopUpSession(amount: number) {
    return firstValueFrom(
      this.http.post<{ url: string }>(this.url + `stripe/create-topup`, { amount })
    );
  }

  async requestTopupDetails(sessionId: string) {
    return firstValueFrom(
      this.http.get<{ id: number, value: string }>(this.url + `stripe/status?session_id=${sessionId}`)
    );
  }

  async getAdminNewsletter(term = '', page = 1, sorting = 'n.asc', pageSize = 30) {
    if (!sorting) {
      sorting = 'n.asc';
    }
    const params = `?&term=${term}&pageSize=${pageSize}&currPage=${page}&sorting=${sorting}`;

    return firstValueFrom(
      this.http.get<IPagingData<IAdminNewsletter>>(this.url + `admin/newsletter` + params)
    )
  }

  saveAdminNewsletter(newsletter: IAdminNewsletter) {
    return firstValueFrom(
      this.http.post<IIdResponse>(this.url + `admin/newsletter`, newsletter)
    );
  }

  deleteAdminNewsletter(id: number) {
    return firstValueFrom(
      this.http.delete<void>(this.url + `admin/newsletter/${id}`)
    );
  }

  async sendTestNewsletter(slug: string) {
    return firstValueFrom(
      this.http.get<void>(this.url + `admin/newsletter/${slug}/test`)
    )
  }

  /** Admin dashboard stats. Dates are YYYY-MM-DD (UTC) */
  async getAdminDashboard(from: string, to: string) {
    return firstValueFrom(
      this.http.get<IAdminDashboard>(this.url + `admin/dashboard?from=${from}&to=${to}`)
    );
  }
}

